import React, { useState, useRef, useEffect } from "react";

export interface ComboboxOption {
  label: string;
  value: string;
  [key: string]: unknown;
}

interface ComboboxProps {
  id?: string;
  value: string;
  options: ComboboxOption[];
  onInputChange: (val: string) => void;
  onSelect: (option: ComboboxOption | null) => void;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
}

export const Combobox: React.FC<ComboboxProps> = ({
  id,
  value,
  options,
  onInputChange,
  onSelect,
  placeholder,
  loading,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  function handleInput(e: React.ChangeEvent<HTMLInputElement>) {
    setInputValue(e.target.value);
    onInputChange(e.target.value);
    setOpen(true);
  }

  function handleSelect(option: ComboboxOption) {
    onSelect(option);
    setInputValue("");
    setOpen(false);
  }

  function handleBlur() {
    // Delay to allow click on option
    setTimeout(() => setOpen(false), 100);
  }

  function handleFocus() {
    if (!disabled) setOpen(true);
  }

  return (
    <div className="relative w-full">
      <input
        id={id}
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInput}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-5"
      />
      {loading && (
        <div className="absolute right-2 top-2 text-xs text-muted-foreground">Loading...</div>
      )}
      {open && options.length > 0 && !disabled && (
        <ul
          ref={listRef}
          className="absolute z-10 w-full bg-background border rounded shadow mt-1 max-h-48 overflow-auto"
        >
          {options.map(option => (
            <li key={option.value}>
              <button
                type="button"
                className="w-full text-left px-2 py-1 hover:bg-muted"
                onMouseDown={() => handleSelect(option)}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
